/**
 * Owns the price-edition read path: fetch, cache, revalidate, publish, revert.
 *
 * The critical distinction is "no editions" vs "fetch failed". Under per-vendor
 * data gating these look alike but mean opposite things — DELL legitimately has
 * zero editions, while AIC having zero after a publish means something is wrong.
 * A successful fetch returning nothing is an ANSWER and is cached as one, which
 * is what makes the failed-fetch rows below safe.
 *
 * `history`: when true, the INITIAL load fetches limit: 50 instead of the
 * default limit: 1. Task 13's /admin/prices page renders `editions` as the
 * full history — with only one edition loaded its diff would always compare
 * against null and revert would never appear (revert is gated on
 * `e.editionNumber !== editions[0].editionNumber`). App.js (the rep-facing
 * configurator) keeps the default so no rep pays for a 50-edition fetch;
 * the admin page opts in. A post-publish/revert reload always uses
 * `load({ all: true })` regardless of `history`, so `all` and `history` are
 * ORed together into one effective limit — one fetch, not two.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import { createPriceEdition, priceEditionsByVendor } from '../graphql/priceEditionOps';
import { readCache, writeCache } from '../pricing/editionCache';
import { seedFromRows } from '../pricing/rowsToSnapshot';
import { buildSheet } from '../pricing/editionSheet';
import { planPublish, planRevert } from '../pricing/publishPlan';
import { publishQuarters, productDbFor } from '../pricing/publishQuarters';
import { checkPublishCoverage, formatCoverageFailure } from '../pricing/publishCoverage';
import { fetchCatalogRows } from '../utils/catalogFetch';

const REVALIDATE_MS = 5 * 60 * 1000;

/** Wire shape -> EditionRecord: `sheet` arrives as an AWSJSON string. */
function toRecord(item) {
  return { ...item, sheet: typeof item.sheet === 'string' ? JSON.parse(item.sheet) : item.sheet };
}

export function usePriceEditions(vendor, { history = false } = {}) {
  const [edition, setEdition] = useState(null);
  const [editions, setEditions] = useState([]);
  const [status, setStatus] = useState('loading');
  const [staleWarning, setStaleWarning] = useState(false);
  const lastFetch = useRef(0);
  const fetchedFor = useRef(null);

  const load = useCallback(async ({ all = false } = {}) => {
    if (!vendor) return;
    const key = vendor;
    fetchedFor.current = key;
    lastFetch.current = Date.now();
    try {
      const client = generateClient();
      const res = await client.graphql({
        query: priceEditionsByVendor,
        variables: { vendor, sortDirection: 'DESC', limit: all || history ? 50 : 1 },
      });
      if (fetchedFor.current !== key) return;
      const items = res.data.priceEditionsByVendor.items.map(toRecord);
      setEditions(items);
      if (items.length === 0) {
        writeCache(vendor, { kind: 'none' });
        setEdition(null); setStaleWarning(false); setStatus('ready');
      } else {
        writeCache(vendor, { kind: 'edition', edition: items[0] });
        setEdition(items[0]); setStaleWarning(false); setStatus('ready');
      }
    } catch (e) {
      console.error('Price edition fetch failed:', e);
      if (fetchedFor.current !== key) return;
      const cached = readCache(vendor);
      if (cached?.kind === 'edition') {
        setEdition(cached.edition); setEditions([cached.edition]);
        setStaleWarning(true); setStatus('stale');
      } else if (cached?.kind === 'none') {
        setEdition(null); setEditions([]); setStaleWarning(false); setStatus('ready');
      } else {
        // Nothing cached and no answer from the server: we cannot prove
        // productDb is current, and no prices beats wrong prices.
        setEdition(null); setEditions([]); setStaleWarning(false); setStatus('error');
      }
    }
  }, [vendor, history]);

  // Vendor (or history) change: reset to the initial, unanswered state BEFORE
  // kicking off the new load. Without this, the previous vendor's edition/
  // editions/status linger for the whole in-flight window — and if the new
  // fetch then fails, the catch block above updates edition/status but never
  // touched editions, so a vendor switch could leave `editions` holding the
  // PREVIOUS vendor's history under the new vendor's identity (wrong history
  // in Task 13's admin view; a stale sheet merged over the new vendor's
  // productDb in App.js). This effect only fires when `load`'s identity
  // changes (i.e. vendor/history actually changed), never on a manual
  // refresh()/revalidate call, so it doesn't cause loading-state flicker on
  // an ordinary background refetch.
  useEffect(() => {
    setEdition(null); setEditions([]); setStatus('loading'); setStaleWarning(false);
    load();
  }, [load]);

  // Revalidate on tab focus, throttled — publishes are quarterly, but this
  // bounds how long two reps can hold different sheets.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastFetch.current < REVALIDATE_MS) return;
      load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  const publish = useCallback(async ({ publishedBy, appVersion } = {}) => {
    const { components, compositions } = await fetchCatalogRows(vendor);
    // Guard against publishing against an empty (or effectively empty) catalog.
    // buildSheet deliberately does NOT throw on a per-quarter empty resolution
    // (a legitimately-unpriced early quarter is normal), so nothing downstream
    // of it would otherwise notice "the whole catalog is empty" — planPublish
    // would happily write a well-formed edition with an empty sheet, and
    // priceBookFromEdition strips every productDb pricing key and restores
    // nothing, zeroing every hardware price for every rep immediately. There is
    // no confirmation dialog on publish and no earlier edition to revert to, so
    // this must be impossible rather than merely confirmable.
    if (components.length === 0) {
      throw new Error(
        `Cannot publish ${vendor}: the catalog has no component rows. Publishing ` +
        `now would zero every hardware price for every rep. Seed the catalog before publishing.`
      );
    }
    const now = new Date().toISOString();
    // Hoisted out of the buildSheet call so the coverage guard below sees the
    // EXACT seed, quarter list and asOf this sheet was built from. A guard that
    // re-derives its own inputs can pass while the artefact being published
    // disagrees with it.
    //
    // Quarters come from publishQuarters, never from the catalog's own rows:
    // sparse rows mean a quarter nothing changed in has no row at all and would
    // silently vanish from the sheet.
    const seed = seedFromRows(components, compositions);
    const quarters = publishQuarters(vendor);
    const sheet = buildSheet(seed, quarters, now);
    // Second guard: rows are present but resolved to no priced SKUs at all
    // (e.g. a catalog with only non-hardware rows, or rows that never satisfy
    // a full composition). Same hazard as the empty-catalog case above.
    if (Object.keys(sheet).length === 0) {
      throw new Error(
        `Cannot publish ${vendor}: the catalog produced no priced SKUs across any ` +
        `quarter. Publishing now would zero every hardware price for every rep. ` +
        `Check the catalog data before publishing.`
      );
    }
    // Third guard — invariant C (model spec §3.3). The two above are
    // all-or-nothing; this is the per-SKU check they cannot make. A SKU present
    // in productDb but absent from the sheet prices at $0 for every rep, with
    // no error and no banner, because priceBookFromEdition strips the productDb
    // key and getProductPrice returns 0 for a missing one. Deliberate
    // retirement (an 'unpriced' terminator) is exempt; sparse authoring is
    // normal and never trips this.
    const { violations, excused } = checkPublishCoverage({
      sheet, seed, quarters, productDb: productDbFor(vendor), asOf: now,
    });
    if (violations.length > 0) throw new Error(formatCoverageFailure(vendor, violations));
    const record = planPublish(vendor, edition, sheet, {
      asOf: now, publishedAt: now, publishedBy, appVersion,
      sourceNote: edition ? 'republish current catalog' : 'initial publish',
    });
    const client = generateClient();
    await client.graphql({
      query: createPriceEdition,
      variables: { input: { ...record, sheet: JSON.stringify(record.sheet) } },
    });
    await load({ all: true });
    // A terminator EXCUSES a SKU from the guard, and the carve-out matches per
    // component — one 'unpriced' row on CMP-LABOR-NODE excuses every node SKU at
    // once. That is allowed on purpose (retiring a family through a shared
    // component is legitimate), but it must not be silent, so the excused list
    // rides back with the record for EditionsPage to show.
    return { ...record, coverageExcused: excused };
  }, [vendor, edition, load]);

  /**
   * Deliberately NOT coverage-guarded (model spec §3.3 covers publish only).
   * Revert republishes a sheet that was already published, and its job is
   * recovery from a bad publish. An older edition can legitimately predate a
   * SKU productDb prices today, so guarding revert would block the recovery
   * path in exactly the situation it exists for. The residual risk is real:
   * reverting to that older edition can silently zero any SKU introduced
   * since it, relative to what the currently-live edition prices. That is
   * accepted because refusing would break the recovery path it exists for;
   * surfacing those SKUs as a warning (rather than a refusal) is a known
   * follow-up, not yet built.
   */
  const revert = useCallback(async (target, { publishedBy, appVersion }) => {
    const now = new Date().toISOString();
    const record = planRevert(vendor, edition, target, {
      asOf: now, publishedAt: now, publishedBy, appVersion,
    });
    const client = generateClient();
    await client.graphql({
      query: createPriceEdition,
      variables: { input: { ...record, sheet: JSON.stringify(record.sheet) } },
    });
    await load({ all: true });
    return record;
  }, [vendor, edition, load]);

  return {
    edition, editions, status, staleWarning,
    editionNumber: edition?.editionNumber ?? null,
    refresh: load, publish, revert,
  };
}

/**
 * Whether a newly-fetched edition should raise the "New prices available"
 * banner instead of being adopted silently.
 *
 * Silent when the configurator is pristine; prompt once the user has work in
 * progress. Never prompt on first load — there is no previous edition to have
 * moved away from, and a banner there would be meaningless.
 */
export function shouldPromptForNewEdition({ touched, previous, next }) {
  if (next === null || next === undefined) return false;
  if (previous === null || previous === undefined) return false;
  if (previous === next) return false;
  return Boolean(touched);
}
